import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Megaphone, Plus, Loader2, Paperclip, Download, Trash2, Building2, Clock3 } from "lucide-react";
import { toast } from "sonner";
import { ROLE_LABEL } from "@/lib/format";

const timeLeft = (expiresAt) => {
  if (!expiresAt) return "";
  const ms = new Date(expiresAt) - new Date();
  if (ms <= 0) return "Expired";
  const h = Math.floor(ms / 3600000);
  const d = Math.floor(h / 24);
  if (d > 0) return `${d}d ${h % 24}h left`;
  return `${h}h left`;
};

export default function Notices() {
  const { user } = useAuth();
  const canPost = ["admin", "manager", "op_manager", "hod"].includes(user?.role);
  const [notices, setNotices] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [postOpen, setPostOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [n, o] = await Promise.all([
        api.get("/notices"),
        api.get("/outlets").catch(() => ({ data: [] })),
      ]);
      setNotices(n.data);
      setOutlets(o.data.filter((x) => !x.is_internal));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onDelete = async (n) => {
    if (!confirm(`Delete "${n.title}"? Its attachment will also be removed.`)) return;
    try {
      await api.delete(`/notices/${n.id}`);
      setNotices((prev) => prev.filter((x) => x.id !== n.id));
      toast.success("Notice deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const outletLabel = useCallback((ids) => {
    if (!ids?.length) return "All outlets";
    return ids.map((id) => outlets.find((o) => o.id === id)?.code || id.slice(0, 4)).join(", ");
  }, [outlets]);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 inline-flex items-center gap-2">
            <Megaphone className="w-6 h-6 text-indigo-600" /> Notices & Circulars
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Notices auto-expire 7 days after posting.</p>
        </div>
        {canPost && (
          <Dialog open={postOpen} onOpenChange={setPostOpen}>
            <DialogTrigger asChild>
              <Button data-testid="notice-open-post" className="bg-indigo-600 hover:bg-indigo-700 text-white h-11">
                <Plus className="w-4 h-4 mr-1.5" /> Post Notice
              </Button>
            </DialogTrigger>
            <PostNoticeDialog user={user} outlets={outlets} onDone={() => { setPostOpen(false); load(); }} />
          </Dialog>
        )}
      </div>

      {loading && (
        <div className="py-12 text-center"><Loader2 className="w-5 h-5 mx-auto text-slate-400 animate-spin" /></div>
      )}

      {!loading && notices.length === 0 && (
        <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-white p-10 text-center">
          <Megaphone className="w-10 h-10 text-slate-300 mx-auto" />
          <div className="mt-3 font-semibold text-slate-800">No notices yet</div>
          <div className="text-xs text-slate-500 mt-1">
            {canPost ? "Post a notice to reach your team instantly." : "You're all caught up."}
          </div>
        </div>
      )}

      <div className="grid gap-3">
        {notices.map((n) => (
          <article
            key={n.id}
            data-testid={`notice-card-${n.id}`}
            className="rounded-2xl bg-white border border-slate-200 p-4 shadow-sm hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <h3 className="font-semibold text-slate-900 text-base break-words">{n.title}</h3>
                <div className="text-[11px] text-slate-500 mt-0.5 flex items-center flex-wrap gap-x-2 gap-y-0.5">
                  <span>{n.created_by_name}</span>
                  <span className="text-slate-300">·</span>
                  <span>{ROLE_LABEL[n.created_by_role] || n.created_by_role}</span>
                  <span className="text-slate-300">·</span>
                  <span className="inline-flex items-center gap-1"><Building2 className="w-3 h-3" /> {n.audience_type === "all" ? "All outlets" : outletLabel(n.outlet_ids)}</span>
                  <span className="text-slate-300">·</span>
                  <span className="inline-flex items-center gap-1 text-amber-700"><Clock3 className="w-3 h-3" /> {timeLeft(n.expires_at)}</span>
                </div>
              </div>
              {(user?.role === "admin" || n.created_by === user?.id) && (
                <Button
                  variant="ghost"
                  size="icon"
                  data-testid={`notice-delete-${n.id}`}
                  onClick={() => onDelete(n)}
                  className="text-rose-600 hover:bg-rose-50 h-8 w-8 shrink-0"
                  title="Delete notice"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
            <p className="mt-2 text-sm text-slate-700 whitespace-pre-wrap break-words">{n.message}</p>
            {n.file_id && (
              <a
                data-testid={`notice-download-${n.id}`}
                href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${n.file_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-700 hover:text-indigo-900 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded-md px-2.5 py-1.5"
              >
                <Paperclip className="w-3.5 h-3.5" /> Attachment
                <Download className="w-3.5 h-3.5 ml-0.5" />
              </a>
            )}
          </article>
        ))}
      </div>
    </div>
  );
}

function PostNoticeDialog({ user, outlets, onDone }) {
  const isHOD = user?.role === "hod";
  const canPickAll = user?.role === "admin" || user?.role === "manager" || user?.role === "op_manager";
  const availableOutlets = useMemo(
    () => (isHOD ? outlets.filter((o) => (user?.outlet_ids || []).includes(o.id)) : outlets),
    [outlets, isHOD, user],
  );
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [audience, setAudience] = useState(canPickAll ? "all" : "outlets");
  const [chosen, setChosen] = useState([]);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!title.trim() || !message.trim()) { toast.error("Title and message are required"); return; }
    if (audience === "outlets" && chosen.length === 0) { toast.error("Pick at least one outlet"); return; }
    setBusy(true);
    try {
      let fileId = null;
      if (file) {
        const fd = new FormData(); fd.append("file", file);
        const { data: up } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        fileId = up.id;
      }
      const { data } = await api.post("/notices", {
        title: title.trim(),
        message: message.trim(),
        file_id: fileId,
        audience_type: audience,
        outlet_ids: audience === "outlets" ? chosen : [],
      });
      toast.success(`Notice posted — ${data.recipient_count} people notified`);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to post notice");
    } finally { setBusy(false); }
  };

  return (
    <DialogContent className="max-w-lg">
      <DialogHeader><DialogTitle>Post a notice</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div>
          <Label>Title</Label>
          <Input data-testid="notice-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Diwali stock deadline" className="mt-1.5" />
        </div>
        <div>
          <Label>Message</Label>
          <Textarea data-testid="notice-message" value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Details of the notice" className="mt-1.5 min-h-28" />
        </div>
        <div>
          <Label>Audience</Label>
          <Select value={audience} onValueChange={setAudience} disabled={isHOD}>
            <SelectTrigger data-testid="notice-audience" className="mt-1.5"><SelectValue /></SelectTrigger>
            <SelectContent>
              {canPickAll && <SelectItem value="all">All outlets (org-wide)</SelectItem>}
              <SelectItem value="outlets">Specific outlets</SelectItem>
            </SelectContent>
          </Select>
          {isHOD && <div className="text-[11px] text-slate-500 mt-1">HOD notices go to your allotted outlets only.</div>}
        </div>
        {audience === "outlets" && (
          <div className="max-h-40 overflow-y-auto space-y-1 border border-slate-200 rounded-md p-2">
            {availableOutlets.length === 0 && <div className="text-[11px] text-slate-500 p-2 text-center">No outlets available</div>}
            {availableOutlets.map((o) => (
              <label key={o.id} className="flex items-center gap-2 rounded-md p-1.5 cursor-pointer hover:bg-slate-50">
                <input
                  type="checkbox"
                  data-testid={`notice-outlet-${o.id}`}
                  checked={chosen.includes(o.id)}
                  onChange={(e) => setChosen((prev) => e.target.checked ? [...prev, o.id] : prev.filter((x) => x !== o.id))}
                />
                <span className="text-sm text-slate-800"><span className="font-semibold">{o.code}</span> · {o.name}</span>
              </label>
            ))}
          </div>
        )}
        <div>
          <Label>Attachment (PDF or image, optional)</Label>
          <input
            type="file"
            data-testid="notice-file"
            accept="application/pdf,image/*"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="mt-1.5 block w-full text-xs file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
          />
        </div>
      </div>
      <DialogFooter>
        <Button data-testid="notice-submit" onClick={submit} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700 text-white">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Post & notify"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
